import { useNavigate } from 'react-router-dom';
import { TEMPLATES, type Template } from './data';
import { useEditorStore } from '@/features/editor/store';

// TemplateGallery surfaces 5 starter architectures on the dashboard.
// Click → loads the diagram into the editor store and navigates to
// /editor. The prompt drawer is seeded with the template's description
// so follow-up tweaks feel natural ("add a CDN", "swap ECS for Lambda").

interface Props {
  title?: string;
}

export function TemplateGallery({ title = 'Start from a template' }: Props) {
  const loadDiagram = useEditorStore((s) => s.loadDiagram);
  const navigate = useNavigate();

  const open = (tpl: Template) => {
    loadDiagram(tpl.diagram);
    // Persist the prompt so the editor's prompt drawer picks it up on mount.
    try {
      sessionStorage.setItem('clyra-initial-prompt', tpl.prompt);
    } catch {
      // sessionStorage quota exceeded is fine — the template still loads.
    }
    navigate('/editor');
  };

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-base font-semibold text-ink-primary [body.paper_&]:text-charcoal">
            {title}
          </h2>
          <p className="mt-1 text-petit text-ink-muted [body.paper_&]:text-charcoal-muted">
            Open one, then edit — these are real diagrams, not stubs.
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => open(t)}
            className="group relative overflow-hidden rounded-xl border border-line-subtle bg-clyra-raised p-4 text-left transition-colors hover:border-line-default [body.paper_&]:border-stone-subtle [body.paper_&]:bg-paper-raised [body.paper_&]:hover:border-stone-default"
          >
            <div className="flex items-center justify-between">
              <TagPill tag={t.tag} />
              <span className="text-micro text-ink-faint group-hover:text-ink-muted [body.paper_&]:text-charcoal-faint [body.paper_&]:group-hover:text-charcoal-muted">
                {t.diagram.nodes.length} nodes · {t.diagram.edges.length} edges
              </span>
            </div>
            <h3 className="mt-3 text-sm font-semibold text-ink-primary [body.paper_&]:text-charcoal">
              {t.name}
            </h3>
            <p className="mt-1 text-petit text-ink-secondary leading-relaxed [body.paper_&]:text-charcoal-soft">
              {t.summary}
            </p>
            <div className="mt-4 text-tiny text-brand-400 group-hover:text-brand-300 [body.paper_&]:text-teal-600 [body.paper_&]:group-hover:text-teal-500">
              Open →
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

const TAG_STYLES: Record<Template['tag'], string> = {
  starter: 'bg-teal-50 text-teal-700 [body:not(.paper)_&]:bg-brand-500/15 [body:not(.paper)_&]:text-brand-300',
  data:    'bg-amber-50 text-amber-700 [body:not(.paper)_&]:bg-amber-500/15 [body:not(.paper)_&]:text-amber-300',
  app:     'bg-emerald-50 text-emerald-700 [body:not(.paper)_&]:bg-emerald-500/15 [body:not(.paper)_&]:text-emerald-300',
  ai:      'bg-violet-50 text-violet-700 [body:not(.paper)_&]:bg-violet-500/15 [body:not(.paper)_&]:text-violet-300',
};

function TagPill({ tag }: { tag: Template['tag'] }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-micro font-medium uppercase tracking-wider ${TAG_STYLES[tag]}`}>
      {tag}
    </span>
  );
}
